import { useEffect, useRef } from 'react';

/**
 * WaterSwab — pool / Caribbean-cyan water surface.
 *
 * Self-contained fragment shader (no fluid sim). Layered effects:
 *   - Depth-faded cyan base, brighter at the top, deeper teal at the bottom.
 *   - Classic looped-noise caustics (Dave Hoskins / Shadertoy lineage) to fake
 *     the rippling refracted sunlight pattern from above.
 *   - Tiny domain-warped wave normals modulating a fake specular highlight.
 *   - Fresnel rim glow around the canvas edges.
 *   - Cursor ripples — concentric rings of brightness emanating from where
 *     the mouse moves over the surface.
 */

const VERT = `
attribute vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;        // 0..1
uniform float u_mouseActive;
uniform float u_lastSplatT;  // u_time when cursor last moved
uniform vec2 u_lastSplatPos; // 0..1 where it moved

// Caustic loop adapted from the classic Shadertoy formula — multi-sample
// trig domain warp that converges on a sharp, sun-through-wavy-water look.
float caustic(vec2 uv, float t) {
  vec2 p = mod(uv * 6.2831, 6.2831) - 250.0;
  vec2 i = p;
  float c = 1.0;
  const float inten = 0.0045;
  for (int n = 0; n < 5; n++) {
    float ti = t * 0.5 + float(n);
    i = p + vec2(cos(ti - i.x) + sin(ti + i.y), sin(ti - i.y) + cos(ti + i.x));
    c += 1.0 / length(vec2(p.x / (sin(i.x + ti) / inten), p.y / (cos(i.y + ti) / inten)));
  }
  c /= 5.0;
  c = 1.17 - pow(c, 1.4);
  return clamp(pow(abs(c), 7.0), 0.0, 1.0);
}

// Cheap value noise for wave normals.
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float waves(vec2 uv, float t) {
  return vnoise(uv * 4.0 + vec2(t * 0.4, t * 0.25))
       + 0.5 * vnoise(uv * 8.0 + vec2(-t * 0.3, t * 0.5));
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 p = uv - 0.5;
  float t = u_time;

  // --- Wave height + finite-diff "normal" for a fake specular ---
  float e = 0.004;
  float h  = waves(uv, t);
  float hx = waves(uv + vec2(e, 0.0), t);
  float hy = waves(uv + vec2(0.0, e), t);
  vec3 normal = normalize(vec3((hx - h) / e, (hy - h) / e, 1.5));

  // --- Base cyan: Caribbean-pool turquoise with a gentle depth shimmer ---
  vec3 shallow = vec3(0.55, 0.92, 0.95);   // bright turquoise highlights
  vec3 pool    = vec3(0.15, 0.72, 0.85);   // signature pool cyan
  vec3 baseCol = mix(pool, shallow, h * 0.6); // wave height adds shimmer

  // --- Caustics across the whole surface for the dappled sunlight look ---
  float caus = caustic(uv + 0.04 * normal.xy, t);
  vec3 col = baseCol + vec3(0.9, 1.0, 1.0) * caus * 0.7;

  // --- Soft sun glint off wave normals (kept gentle so it doesn't streak) ---
  vec3 sunDir = normalize(vec3(0.3, 0.5, 0.9));
  float spec = pow(max(0.0, dot(normal, sunDir)), 32.0);
  col += vec3(1.0) * spec * 0.25;

  // --- Tiny fresnel lift at the edges only ---
  float edge = smoothstep(0.62, 0.78, length(p));
  col = mix(col, vec3(0.75, 0.95, 1.0), edge * 0.18);

  // --- Cursor ripples: a moving wavefront radiating from the last move ---
  float ripple = 0.0;
  if (u_mouseActive > 0.001) {
    float age = max(0.0, t - u_lastSplatT);
    float age2 = age * 2.0;
    float d = length(uv - u_lastSplatPos);
    // Two concentric expanding wavefronts with quick decay.
    float r1 = sin(40.0 * d - age2 * 6.0) * exp(-d * 8.0) * exp(-age * 1.5);
    float r2 = sin(28.0 * d - age2 * 4.0) * exp(-d * 6.0) * exp(-age * 1.1);
    ripple = (r1 + 0.5 * r2) * u_mouseActive;
  }
  col += vec3(1.0) * ripple * 0.18;

  // Cursor proximity also lifts the surface a touch — light dome.
  float md = length(uv - u_mouse);
  col += vec3(0.6, 0.9, 1.0) * exp(-md * 9.0) * u_mouseActive * 0.12;

  // Final gentle tonemap / saturation tweak.
  col = pow(col, vec3(0.95));
  gl_FragColor = vec4(col, 1.0);
}
`;

export default function WaterSwab() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: 0, lastSplatT: 0, lastX: 0.5, lastY: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
    if (!gl) return;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('Water shader error:', gl.getShaderInfoLog(sh));
      }
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const timeLoc = gl.getUniformLocation(program, 'u_time');
    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    const mouseLoc = gl.getUniformLocation(program, 'u_mouse');
    const activeLoc = gl.getUniformLocation(program, 'u_mouseActive');
    const splatTLoc = gl.getUniformLocation(program, 'u_lastSplatT');
    const splatPosLoc = gl.getUniformLocation(program, 'u_lastSplatPos');

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const w = Math.floor(rect.width * dpr);
      const h = Math.floor(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const start = performance.now();
    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1 - (e.clientY - rect.top) / rect.height;
      const m = mouseRef.current;
      m.x = x;
      m.y = y;
      // Trigger a new ripple only when the cursor moves noticeably so the
      // surface doesn't pulse on tiny jitters.
      const moved = Math.hypot(x - m.lastX, y - m.lastY);
      if (moved > 0.01) {
        m.lastSplatT = (performance.now() - start) / 1000;
        m.lastX = x;
        m.lastY = y;
      }
    };
    const onEnter = () => { mouseRef.current.active = 1; };
    const onLeave = () => { mouseRef.current.active = 0; };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseenter', onEnter);
    canvas.addEventListener('mouseleave', onLeave);

    let raf = 0;
    let activeEased = 0;
    const render = () => {
      resize();
      const m = mouseRef.current;
      activeEased += (m.active - activeEased) * 0.12;
      gl.uniform1f(timeLoc, (performance.now() - start) / 1000);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.uniform2f(mouseLoc, m.x, m.y);
      gl.uniform1f(activeLoc, activeEased);
      gl.uniform1f(splatTLoc, m.lastSplatT);
      gl.uniform2f(splatPosLoc, m.lastX, m.lastY);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseenter', onEnter);
      canvas.removeEventListener('mouseleave', onLeave);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full aspect-square rounded-xl bg-cyan-900" />;
}
